import React from 'react'
import { Typography, Divider, Chip } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import SettingsIcon from '@mui/icons-material/Settings'
import PersonIcon from '@mui/icons-material/Person'
import ProfileAvatar, { profileAvatarPropsOf } from '../../../ProfileAvatar'
import type { UserData } from '../../../../types/user'
import {
  ContentHeader,
  PageTitle,
  ActionButton,
  ProfileContainer,
  ProfileSection,
  ProfileHeader,
  LargeAvatar,
  ProfileDetails,
  ProfileSectionTitle,
  ProfileField,
  FieldLabel,
  FieldValue,
} from '../../../styles/MainMenu.styles'
import { getRoleColor } from '../../../CompanyManagement/types'
import { getDisplayName } from '../../../../utils/personName'
import { useRbacStore } from '../../../../stores/rbacStore'
import { memberRoleLabel } from '../../../CompanyManagement/utils/roleOptions'

function getDisplayFullName(user: UserData | null): string {
  return getDisplayName(user)
}

/** Company roles as people read them; a bare "admin" would not say whether of the company or the platform. */
export const COMPANY_ROLE_LABELS: Record<string, string> = {
  admin: 'Company admin',
  manager: 'Company manager',
  moderator: 'Moderator',
  member: 'Member',
  user: 'Member',
}

export interface ProfileMainMenuPanelProps {
  userData: UserData | null
  /** The user's role in their company (`CompanyUser.role`), as the Users list shows it. */
  companyRole?: string | null
  /** The role's current name, when cloud sent it with the company answer. */
  companyRoleName?: string | null
  /** Cloud says the role is the fallback because none is stored for the user: marked "(default)". */
  companyRoleIsDefault?: boolean
  /** Server-verified platform admin. Platform admins hold no company role, so this badge replaces it. */
  isPlatformAdmin?: boolean
  onEditProfile: () => void
}

export default function ProfileMainMenuPanel({ userData, companyRole, companyRoleName, companyRoleIsDefault = false, isPlatformAdmin = false, onEditProfile }: ProfileMainMenuPanelProps) {
  const theme = useTheme()
  const displayName = getDisplayFullName(userData)
  const role = companyRole || 'member'
  // The key and the system role names come from what the RBAC store already holds; nothing is
  // requested here, so a custom role's name needs `companyRoleName`.
  const roleKey = useRbacStore((s) => s.me?.roleKey)
  const knownRoles = useRbacStore((s) => s.catalogue?.systemRoles)
  // Same wording as the chip by the log out button.
  const roleLabel = isPlatformAdmin
    ? 'Global Admin'
    : memberRoleLabel(
        { roleKey, role_name: companyRoleName, role_is_default: companyRoleIsDefault },
        COMPANY_ROLE_LABELS[role] || role,
        knownRoles
      )
  const roleColor = isPlatformAdmin ? theme.palette.primary.main : getRoleColor(role)

  return (
    <>
      <ContentHeader>
        <PageTitle>My Profile</PageTitle>
        <ActionButton
          variant="contained"
          color="primary"
          startIcon={<SettingsIcon />}
          onClick={onEditProfile}
        >
          Edit Profile
        </ActionButton>
      </ContentHeader>

      <ProfileContainer>
        <ProfileSection>
          <ProfileHeader>
            <LargeAvatar>
              <ProfileAvatar {...profileAvatarPropsOf(userData)} name={displayName} size={80} />
            </LargeAvatar>
            <ProfileDetails>
              <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                {displayName}
              </Typography>
              <Chip
                label={roleLabel}
                size="small"
                data-testid="profile-role"
                sx={{
                  bgcolor: alpha(roleColor, 0.12),
                  color: roleColor,
                  fontWeight: 600,
                  mb: 2,
                }}
              />
              <Typography variant="body2" sx={{ color: '#757575' }}>
                Member since {new Date(userData?.createdAt || '').toLocaleDateString()}
              </Typography>
            </ProfileDetails>
          </ProfileHeader>

          <Divider sx={{ my: 3, borderColor: '#eeeeee' }} />

          <ProfileSectionTitle>
            <PersonIcon sx={{ fontSize: 20 }} />
            Personal Information
          </ProfileSectionTitle>

          <ProfileField>
            <FieldLabel>Email</FieldLabel>
            <FieldValue>{userData?.email || 'Not set'}</FieldValue>
          </ProfileField>

          <ProfileField>
            <FieldLabel>Title</FieldLabel>
            <FieldValue>{userData?.title || 'Not set'}</FieldValue>
          </ProfileField>

          <ProfileField>
            <FieldLabel>Bio</FieldLabel>
            <FieldValue>{userData?.bio || 'No bio provided'}</FieldValue>
          </ProfileField>

          <ProfileField>
            <FieldLabel>Contact</FieldLabel>
            <FieldValue>{userData?.contact || 'Not set'}</FieldValue>
          </ProfileField>

          {userData?.gender && (
            <ProfileField>
              <FieldLabel>Gender</FieldLabel>
              <FieldValue>
                {userData.gender === 'male'
                  ? 'Male'
                  : userData.gender === 'female'
                    ? 'Female'
                    : userData.gender === 'non-binary'
                      ? 'Non-Binary'
                      : userData.gender === 'prefer-not-to-say'
                        ? 'Prefer Not to Say'
                        : userData.gender === 'other'
                          ? 'Other'
                          : userData.gender}
              </FieldValue>
            </ProfileField>
          )}

          {userData?.dateOfBirth && (
            <ProfileField>
              <FieldLabel>Date of Birth</FieldLabel>
              <FieldValue>
                {new Date(userData.dateOfBirth).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </FieldValue>
            </ProfileField>
          )}
        </ProfileSection>
      </ProfileContainer>
    </>
  )
}
